import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, Clock, Sparkles, Gift, MessageCircle } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useOfferStage } from "./PromoBanners";
import { isNativeApp } from "./PayPalCheckout";

const EXCLUDED = ["/consultation", "/admin", "/livechat", "/auth/reset"];
const SESSION_KEY = "astrofate_exit_offer_shown";

// Exit-intent popup — fires once per session when the cursor leaves the top
// of the viewport (desktop). Shows the ₹249 consult offer to recover the visit.
export const ExitIntentOffer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { price, priceOld } = useCurrency();
  const stage = useOfferStage();
  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState("");

  const excluded = EXCLUDED.some(p => location.pathname.startsWith(p)) || stage === 0;

  useEffect(() => {
    if (excluded) return undefined;
    const trigger = () => {
      if (sessionStorage.getItem(SESSION_KEY)) return false;
      sessionStorage.setItem(SESSION_KEY, "1");
      setOpen(true);
      return true;
    };
    // Android app: the native back-press handler calls this right before the app
    // would close, and skips closing when we return true (offer shown).
    window.__astroExitIntent = trigger;
    const onMouseOut = (e) => {
      if (e.clientY > 8 || e.relatedTarget) return;
      trigger();
    };
    // Mobile (touch): exit intent = user scrolled deep, then rapidly scrolls back up
    if (window.matchMedia("(pointer: coarse)").matches) {
      let maxY = 0;
      let lastY = window.scrollY;
      let upDist = 0;
      const onScroll = () => {
        const y = window.scrollY;
        maxY = Math.max(maxY, y);
        if (y < lastY) upDist += lastY - y; else upDist = 0;
        lastY = y;
        if (maxY > 900 && upDist > 400 && y < 500) trigger();
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", onScroll);
        if (window.__astroExitIntent === trigger) delete window.__astroExitIntent;
      };
    }
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      if (window.__astroExitIntent === trigger) delete window.__astroExitIntent;
    };
  }, [excluded]);

  useEffect(() => {
    if (!open) return undefined;
    const calc = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const sec = Math.max(0, Math.floor((midnight - now) / 1000));
      const h = String(Math.floor(sec / 3600)).padStart(2, "0");
      const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
      const s = String(sec % 60).padStart(2, "0");
      setTimer(`${h}:${m}:${s}`);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [open]);

  if (!open || excluded) return null;

  // Inside the Android app the offer is shown as a full-screen poster on back-press.
  const poster = isNativeApp();

  const hasReport = typeof window !== "undefined" && !!window.localStorage.getItem("astrofate_report");
  const goCta = () => {
    setOpen(false);
    if (stage === 3) navigate("/consultation");
    else if (stage === 2) navigate("/chat");
    else navigate(hasReport ? "/chat" : "/kundli");
  };
  const content = stage === 3 ? {
    badge: t("promo.consultBadge"), badgeStyle: { background: "linear-gradient(135deg, #ef4444, #f97316)", color: "white" },
    icon: <Sparkles size={11} />, title: t("promo.exitTitle"), desc: t("promo.exitDesc"),
    price: price("consultation"), oldPrice: priceOld("consultation"), showTimer: true, cta: t("promo.exitCta", { price: price("consultation") }), ctaClass: "btn-purple",
  } : stage === 2 ? {
    badge: t("promo.aiChatBadge"), badgeStyle: { background: "linear-gradient(135deg, #F4B942, #FFD166)", color: "#120021" },
    icon: <MessageCircle size={11} />, title: t("promo.exitAiTitle", { price: price("ai_chat") }), desc: t("promo.exitAiDesc"),
    price: price("ai_chat"), oldPrice: null, showTimer: false, cta: t("promo.aiChatCta", { price: price("ai_chat") }), ctaClass: "btn-gold",
  } : {
    badge: t("promo.exitFreeBadge"), badgeStyle: { background: "linear-gradient(135deg, #F4B942, #FFD166)", color: "#120021" },
    icon: <Gift size={11} />, title: t("promo.exitFreeTitle"), desc: t("promo.exitFreeDesc"),
    price: null, oldPrice: null, showTimer: false, cta: t("promo.freeChatCta"), ctaClass: "btn-gold",
  };

  return (
    <div data-testid="exit-intent-overlay" onClick={() => { if (!poster) setOpen(false); }} style={{
      position: "fixed", inset: 0, zIndex: 9999, background: poster ? "#0a0014" : "rgba(10,0,20,0.75)",
      backdropFilter: poster ? "none" : "blur(6px)", display: "flex", alignItems: "center",
      justifyContent: "center", padding: poster ? 0 : "16px"
    }}>
      <div data-testid={poster ? "exit-intent-poster" : "exit-intent-popup"} onClick={e => e.stopPropagation()} className="animate-fadeInUp exit-popup-card" style={{
        maxWidth: poster ? "100%" : "440px", width: "100%",
        height: poster ? "100%" : "auto",
        borderRadius: poster ? 0 : "20px", position: "relative", overflow: "hidden",
        background: "linear-gradient(160deg, #2D0B45 0%, #120021 70%)",
        border: poster ? "none" : "1px solid rgba(255,209,102,0.35)",
        boxShadow: poster ? "none" : "0 20px 80px rgba(157,78,221,0.35)",
        padding: poster ? "calc(env(safe-area-inset-top) + 56px) 26px calc(env(safe-area-inset-bottom) + 26px)" : "34px 30px 30px",
        textAlign: "center",
        display: poster ? "flex" : "block",
        flexDirection: "column", justifyContent: "center", gap: poster ? "4px" : 0
      }}>
        {poster && (
          <>
            <div aria-hidden style={{ position: "absolute", width: "420px", height: "420px", top: "-140px", right: "-160px", borderRadius: "50%", background: "radial-gradient(circle, rgba(157,78,221,0.35), transparent 70%)", pointerEvents: "none" }} />
            <div aria-hidden style={{ position: "absolute", width: "360px", height: "360px", bottom: "-150px", left: "-140px", borderRadius: "50%", background: "radial-gradient(circle, rgba(255,209,102,0.18), transparent 70%)", pointerEvents: "none" }} />
          </>
        )}
        <button data-testid="exit-intent-close" onClick={() => setOpen(false)} aria-label="Close" style={{
          position: "absolute", top: poster ? "calc(env(safe-area-inset-top) + 14px)" : "12px", right: "12px",
          background: "rgba(255,255,255,0.08)",
          border: "none", borderRadius: "50%", width: poster ? "38px" : "30px", height: poster ? "38px" : "30px", cursor: "pointer",
          color: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2
        }}>
          <X size={poster ? 18 : 15} />
        </button>

        <span style={{ ...content.badgeStyle, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.6px", padding: "5px 14px", borderRadius: "20px", display: "inline-flex", alignItems: "center", gap: "5px", alignSelf: "center" }}>
          {content.icon} {content.badge}
        </span>

        <h2 className="font-cinzel" style={{ color: "#FFD166", fontSize: poster ? "1.9rem" : "1.45rem", margin: poster ? "20px 0 10px" : "16px 0 8px", lineHeight: 1.3 }}>
          {content.title}
        </h2>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: poster ? "0.98rem" : "0.9rem", lineHeight: 1.65, marginBottom: "18px" }}>
          {content.desc}
        </p>

        {content.price && (
          <div style={{ display: "flex", alignItems: "baseline", gap: "10px", justifyContent: "center", marginBottom: "6px" }}>
            <span className="font-cinzel" style={{ color: "#FFD166", fontSize: poster ? "3.1rem" : "2.4rem", fontWeight: 700 }}>{content.price}</span>
            {content.oldPrice && <span style={{ color: "rgba(255,255,255,0.35)", textDecoration: "line-through", fontSize: poster ? "1.35rem" : "1.15rem" }}>{content.oldPrice}</span>}
          </div>
        )}
        {content.showTimer && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center", color: "rgba(255,255,255,0.5)", fontSize: "0.78rem", marginBottom: "20px" }}>
            <Clock size={13} style={{ color: "#fca5a5" }} />
            {t("promo.consultEnds")}
            <span data-testid="exit-intent-countdown" style={{ color: "#fca5a5", fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>{timer}</span>
          </div>
        )}

        <button data-testid="exit-intent-cta" onClick={goCta} className={content.ctaClass} style={{ width: "100%", padding: poster ? "16px" : "13px", fontSize: poster ? "1.02rem" : "0.95rem", marginTop: content.showTimer ? 0 : "6px" }}>
          {content.cta}
        </button>
        <button data-testid="exit-intent-dismiss" onClick={() => setOpen(false)} style={{
          background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: poster ? "0.85rem" : "0.78rem",
          marginTop: poster ? "18px" : "12px", cursor: "pointer", textDecoration: "underline"
        }}>
          {poster ? t("promo.exitDismiss") : t("promo.exitDismiss")}
        </button>
      </div>
    </div>
  );
};
